import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { isEmpty, exportExcel } from '../../utils/util';
import { Table, Input, Button, Select, DatePicker, Tag } from 'antd';
import { TableRowSelection } from 'antd/es/table/interface';
import { api_product_type } from '../../api/product';
import { productType } from '../../model/master';
import { filterOption } from '../../utils/select';
import { 
    DatePresetType, 
    getDateRangeForPreset, 
    isDateInRange, 
    formatDateTH,
    parseDateToYMD
} from '../../utils/date-filter-utils';
import { printDocument } from '../../utils/print-utils';
import { useMeasuredRowScrollY, useTablePagination } from './table-pagination';
import dayjs from 'dayjs';

interface props {
    page: string;
    placeholder: string;
    columns: any;
    data: any;
    filterField: any;
    onAdd: () => void;
    onAddMulti?: () => void;
    enableRowSelection?: boolean;
    serverPagination?: boolean;
    pagination?: any;
    loading?: boolean;
    onSearchChange?: (val: string) => void;
    onProductTypeChange?: (val: number) => void;
    hidePrint?: boolean;
    showDateFilter?: boolean;
    dateField?: string;
    reportTitle?: string;
    onPrint?: (filteredData: any[]) => void;
}

const { Option } = Select;

const CommonTableList: React.FC<props> = (prop) => {
    const navigate = useNavigate();
    const [uri, setUri] = useState("");
    const [search, setSearch] = useState("");
    const [placeholder, setPlaceholder] = useState("");
    const [columns, setColumns] = useState([]);
    const [data, setData] = useState([]);
    const [filterField, setFilterField] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    // Client-side pages got antd's bare default: ten rows and no way to
    // ask for more. Every list screen that renders through this component
    // -- products, members, vendors, employees, payment types, purchase
    // orders, good receives -- now carries the same size changer.
    const clientPagination = useTablePagination();

    // Ten rows, whatever this screen's rows happen to be: a product row is
    // 91px and a good-receive row 55px, so `window.innerHeight - 322` showed
    // six of one and twelve of the other.
    const { ref: tableRef, y: scrollY } = useMeasuredRowScrollY(322);

    const [productType, setProductType] = useState(0);
    const [productTypeList, setProductTypeList] = useState<productType[]>([]);

    // Date Filter State
    const [datePreset, setDatePreset] = useState<DatePresetType>('all');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    // Determine if date filter should be enabled
    const isProductManagementPage = [
        'purchase_order', 'purchase-order', 
        'good_receive', 'good-receive', 
        'check_stock', 'check-stock', 
        'separate', 'include', 'withdraw'
    ].includes(prop.page);

    const showDateFilter = prop.page !== 'product' && (prop.showDateFilter ?? isProductManagementPage);

    const init = async () => {
        if (prop.columns.length > 0) {
            setColumns(prop.columns);
        }

        if (prop.data.length > 0) {
            setData(prop.data);
        } else {
            setData([]);
        }

        if (prop.filterField.length > 0) {
            setFilterField(prop.filterField);
        }

        setPlaceholder(prop.placeholder);
        setUri(prop.page);

        if (prop.page === "product") {
            try {
                let getProductType = await api_product_type('LIST', {});
                setProductTypeList(Array.isArray(getProductType) ? getProductType : []);  
            } catch (e) {
                setProductTypeList([]);
            }
        }
    };

    const onSearch = (event: any) => {
        const val = event.target.value;
        setSearch(val);
        if (prop.serverPagination && prop.onSearchChange) {
            prop.onSearchChange(val);
        }
    };

    const handleDatePreset = (preset: DatePresetType) => {
        setDatePreset(preset);
        if (preset === 'all') {
            setStartDate('');
            setEndDate('');
        } else {
            const range = getDateRangeForPreset(preset);
            setStartDate(range.start);
            setEndDate(range.end);
        }
    };

    const handleCustomDateChange = (startStr: string, endStr: string) => {
        const parsedStart = parseDateToYMD(startStr) || '';
        const parsedEnd = parseDateToYMD(endStr) || '';
        setDatePreset('custom');
        setStartDate(parsedStart);
        setEndDate(parsedEnd);
    };

    const onFilter = () => {
        if (prop.serverPagination) {
            return prop.data || [];
        }

        let filterData = data;

        // 1. Text Search Filter
        if (!isEmpty(search)) {
            const searchUpper = String(search).toUpperCase().trim();
            filterData = filterData.filter((x: any) => {
                return (filterField || []).some((key: string) => {
                    const val = x[key];
                    return val != null && String(val).toUpperCase().includes(searchUpper);
                });
            });
        }

        // 2. Product Type Filter (on product page)
        if (prop.page === "product") {
            if (!isEmpty(productType) && productType > 0) {
                filterData = filterData.filter((x: any) => { 
                    return x['product_type'] === productType;
                });
            }
        }

        // 3. Date Range Filter
        if (showDateFilter && (startDate || endDate)) {
            filterData = filterData.filter((x: any) => {
                const dateVal = prop.dateField 
                    ? x[prop.dateField] 
                    : (x.create_date || x.date || x.doc_date || x.expense_date || x.update_date);
                return isDateInRange(dateVal, startDate, endDate);
            });
        }

        return filterData;
    };

    const printTableReport = (filteredData: any[]) => {
        const printableCols = (prop.columns || []).filter((c: any) => {
            const k = c.key || c.dataIndex;
            return k !== 'operation' && c.title && c.title !== '';
        });

        const titleText = prop.reportTitle || `รายงาน${prop.placeholder || prop.page}`;
        const dateRangeText = datePreset === 'all' 
            ? 'ข้อมูลทั้งหมด (All Dates)' 
            : `${formatDateTH(startDate)} ถึง ${formatDateTH(endDate)} (${
                datePreset === 'today' ? 'วันนี้' :
                datePreset === 'last7days' ? '7 วันที่แล้ว' :
                datePreset === 'this_week' ? 'สัปดาห์นี้' :
                datePreset === 'this_month' ? 'เดือนนี้' : 'กำหนดเอง'
              })`;

        const container = document.createElement('div');
        container.style.padding = '24px';
        container.style.fontFamily = 'Kodchasan, Prompt, sans-serif';
        container.style.color = '#1E293B';
        container.style.background = '#FFFFFF';

        let tableHtml = `
            <div style="display: flex; justify-content: space-between; border-bottom: 2px solid #FF7A59; padding-bottom: 12px; margin-bottom: 16px;">
                <div>
                    <h1 style="font-size: 22px; font-weight: 700; color: #FF7A59; margin: 0;">🥕 JinDa POS Store</h1>
                    <p style="font-size: 11px; color: #64748B; margin: 2px 0 0 0;">ระบบบริหารจัดการร้านค้าปลีก-ส่ง และสต๊อกสินค้า</p>
                </div>
                <div style="text-align: right;">
                    <h2 style="font-size: 18px; font-weight: 700; color: #1E293B; margin: 0;">${titleText}</h2>
                    <p style="font-size: 11px; color: #64748B; margin: 2px 0 0 0;">ช่วงเวลา: <strong>${dateRangeText}</strong></p>
                    <p style="font-size: 11px; color: #64748B; margin: 0;">พิมพ์เมื่อ: ${new Date().toLocaleString('th-TH')}</p>
                </div>
            </div>
            <div style="display: flex; justify-content: space-between; margin-bottom: 12px; font-size: 12px; color: #64748B;">
                <div>จำนวนรายการ: <strong style="color: #1E293B;">${filteredData.length} รายการ</strong></div>
                ${search ? `<div>คำค้นหา: <strong style="color: #1E293B;">"${search}"</strong></div>` : ''}
            </div>
            <table style="width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 20px;">
                <thead>
                    <tr style="background: #FFF3EE; border-bottom: 2px solid #FFCBBB; color: #FF7A59;">
                        <th style="padding: 8px 6px; text-align: center; width: 40px; border: 1px solid #FFD8CC;">#</th>
                        ${printableCols.map((col: any) => `
                            <th style="padding: 8px 10px; text-align: ${col.align || 'left'}; border: 1px solid #FFD8CC;">
                                ${typeof col.title === 'string' ? col.title : col.key || ''}
                            </th>
                        `).join('')}
                    </tr>
                </thead>
                <tbody>
                    ${filteredData.length > 0 ? filteredData.map((row: any, idx: number) => `
                        <tr style="border-bottom: 1px solid #E2E8F0; page-break-inside: avoid;">
                            <td style="padding: 6px; text-align: center; border: 1px solid #E2E8F0;">${idx + 1}</td>
                            ${printableCols.map((col: any) => {
                                let val = row[col.dataIndex || col.key];
                                if (typeof val === 'number') {
                                    val = val.toLocaleString('th-TH');
                                } else if (val == null) {
                                    val = '-';
                                } else {
                                    val = String(val);
                                }
                                return `
                                    <td style="padding: 6px 10px; text-align: ${col.align || 'left'}; border: 1px solid #E2E8F0;">
                                        ${val}
                                    </td>
                                `;
                            }).join('')}
                        </tr>
                    `).join('') : `
                        <tr>
                            <td colspan="${printableCols.length + 1}" style="text-align: center; padding: 20px; color: #94A3B8; border: 1px solid #E2E8F0;">
                                ไม่พบข้อมูลตามเงื่อนไขที่ระบุ
                            </td>
                        </tr>
                    `}
                </tbody>
            </table>
            <div style="display: flex; justify-content: flex-end; margin-top: 30px; font-size: 11px; color: #64748B; page-break-inside: avoid;">
                <div style="text-align: center; width: 220px;">
                    <div style="border-bottom: 1px dotted #94A3B8; height: 40px; margin-bottom: 6px;"></div>
                    <p style="margin: 0;">ผู้พิมพ์รายงาน / วันที่</p>
                </div>
            </div>
        `;
        container.innerHTML = tableHtml;
        printDocument(container, {
            documentType: 'a4-landscape',
            title: titleText
        });
    };

    const onPrint = () => {
        const filtered = onFilter();
        if (prop.onPrint) {
            prop.onPrint(filtered);
        } else {
            printTableReport(filtered);
        }
    };

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection: TableRowSelection<any> = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const onExport = () => {
        let exportData = onFilter();
        if (selectedRowKeys.length > 0) {
            exportData = exportData.filter((x: any) => { return selectedRowKeys.includes(x['key']) });
        }
        exportExcel(uri, exportData);
    };

    const handleProductTypeChange = (e: any) => {
        setProductType(e);
        if (prop.serverPagination && prop.onProductTypeChange) {
            prop.onProductTypeChange(e);
        }
    };

    useEffect(() => {
        init();
    }, [prop.data]);

    const filteredData = onFilter();

    return (
        <>
            <div className='filterBoxList'>
                <div className='filterBox' style={{width: '100%', padding: '0', marginBottom: '0', boxShadow: 'none'}}>
                    <div className='searchBox'>
                        <label>ค้นหา</label><QuestionCircleOutlined rev={undefined} />
                        <Input 
                            className='uppercase'
                            placeholder={`${placeholder}`}
                            value={search}
                            onChange={onSearch}
                        />
                    </div>
                    {prop.page === "product" && (
                        <Select
                            value={productType}
                            showSearch
                            filterOption={filterOption}
                            onChange={handleProductTypeChange}
                            style={{ minWidth: '160px', maxWidth: '220px' }}
                            placeholder="ประเภทสินค้า"
                        >
                            <Option key={0} value={0} code={""}>เลือกประเภท</Option>
                            {(productTypeList || []).map(obj => (
                                <Option key={obj.id} value={obj.id}>{obj.name}</Option>
                            ))}
                        </Select>
                    )}
                    <div className='actionBox'>
                        <Button 
                            className='btn-common' 
                            style={{background: 'var(--secondary, #10B981)', borderColor: 'var(--secondary, #10B981)'}} 
                            type="primary" 
                            onClick={onExport}
                        >
                            <FontAwesomeIcon icon="file-excel" /> Excel
                        </Button>
                        <Button className='btn-common' type="primary" onClick={prop.onAdd}>
                            <FontAwesomeIcon icon="plus" /> เพิ่ม
                        </Button>
                        {prop.onAddMulti && (
                            <Button className='btn-common' type="primary" onClick={prop.onAddMulti}>
                                <FontAwesomeIcon icon="plus" /> เพิ่มหลายรายการ
                            </Button>
                        )}
                        {!prop.hidePrint && (
                            <Button className='btn-cancel' onClick={onPrint} title="พิมพ์รายงานตาราง">
                                <FontAwesomeIcon icon="print" /> พิมพ์
                            </Button>
                        )}
                    </div>
                </div>

                {/* Date Filter & Presets Toolbar (Enabled on Product Management pages) */}
                {showDateFilter && (
                    <div className='table-date-filter-bar'>
                        <div className='date-presets-wrap'>
                            <span style={{ fontSize: '13px', fontWeight: 600, color: '#64748B', marginRight: '4px' }}>
                                <FontAwesomeIcon icon="calendar-days" style={{ marginRight: '6px', color: 'var(--primary, #FF7A59)' }} />
                                ช่วงเวลา:
                            </span>
                            <Button
                                size="small"
                                className={`btn-date-preset ${datePreset === 'all' ? 'active' : ''}`}
                                onClick={() => handleDatePreset('all')}
                            >
                                ทั้งหมด
                            </Button>
                            <Button
                                size="small"
                                className={`btn-date-preset ${datePreset === 'today' ? 'active' : ''}`}
                                onClick={() => handleDatePreset('today')}
                            >
                                วันนี้
                            </Button>
                            <Button
                                size="small"
                                className={`btn-date-preset ${datePreset === 'last7days' ? 'active' : ''}`}
                                onClick={() => handleDatePreset('last7days')}
                            >
                                7 วันที่แล้ว
                            </Button>
                            <Button
                                size="small"
                                className={`btn-date-preset ${datePreset === 'this_week' ? 'active' : ''}`}
                                onClick={() => handleDatePreset('this_week')}
                            >
                                สัปดาห์นี้
                            </Button>
                            <Button
                                size="small"
                                className={`btn-date-preset ${datePreset === 'this_month' ? 'active' : ''}`}
                                onClick={() => handleDatePreset('this_month')}
                            >
                                เดือนนี้
                            </Button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '12px', color: '#64748B' }}>กำหนดเอง:</span>
                            <DatePicker
                                placeholder="จากวันที่"
                                value={startDate ? dayjs(startDate) : null}
                                onChange={(_, dateString) => handleCustomDateChange(String(dateString), endDate)}
                                format="DD/MM/YYYY"
                                style={{ width: '130px', borderRadius: '8px' }}
                            />
                            <span style={{ color: '#94A3B8' }}>-</span>
                            <DatePicker
                                placeholder="ถึงวันที่"
                                value={endDate ? dayjs(endDate) : null}
                                onChange={(_, dateString) => handleCustomDateChange(startDate, String(dateString))}
                                format="DD/MM/YYYY"
                                style={{ width: '130px', borderRadius: '8px' }}
                            />
                            {(startDate || endDate) && (
                                <Tag color="orange" style={{ marginLeft: '4px', borderRadius: '6px', fontWeight: 600 }}>
                                    {filteredData.length} รายการ
                                </Tag>
                            )}
                        </div>
                    </div>
                )}

            </div>
            <div ref={tableRef}>
            <Table
                bordered
                loading={prop.loading}
                columns={columns}
                dataSource={filteredData}
                rowKey={(record: any) => String(record.id ?? record.key ?? Math.random())}
                rowSelection={prop.enableRowSelection ? rowSelection : undefined}
                pagination={prop.serverPagination ? prop.pagination : clientPagination}
                scroll={{ x: 1366, y: scrollY }}
            />
            </div>
        </>
    );
};

export default CommonTableList;
